import { BibliotecaResponse } from './models/biblioteca.models';
import { AppLocale } from '@core/i18n/locale.constants';

export type DatastorySortKey = 'recent' | 'popular' | 'alpha';

export interface StoryCard {
  id: number;
  title: string;
  dek: string;
  link: string;
  author: string;
  dateLabel: string;
  viewsLabel: string;
  views: number;
  publishedAt: number;
  tags: string[];
}

export function formatStoryViews(views: number, locale: AppLocale): string {
  return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'pt-BR').format(views);
}

export function formatStoryDate(iso: string, locale: AppLocale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'pt-BR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const STORY_DEK_BY_ID: Partial<Record<number, Record<AppLocale, string>>> = {
  1: {
    'pt-BR':
      'Análise longitudinal das compras públicas do Poder Executivo Federal entre 2007 e 2018.',
    en: 'Longitudinal analysis of Federal Executive Branch public procurement between 2007 and 2018.',
  },
  2: {
    'pt-BR':
      "Quais foram as mudanças nos cargos da Diretoria de Assessoramento Superior 'DAS' do governo federal nos últimos 30 anos?",
    en: 'What were the changes in Senior Advisory Directorate (DAS) positions in the federal government over the last 30 years?',
  },
  3: {
    'pt-BR': 'Para onde foi o dinheiro discricionário do governo federal?',
    en: 'Where did the federal government’s discretionary money go?',
  },
  13: {
    'pt-BR': 'Como a automação digital afetou os empregos no setor público?',
    en: 'How did digital automation affect jobs in the public sector?',
  },
  15: {
    'pt-BR':
      'Avaliamos as complexidades de leitura dos decretos normativos regulatórios.',
    en: 'We assess the reading complexities of regulatory normative decrees.',
  },
  17: {
    'pt-BR':
      'Como medir o peso de cada setor da economia sobre a produção normativa do Estado?',
    en: 'How to measure the weight of each sector of the economy on the state’s normative production?',
  },
  19: {
    'pt-BR':
      'Organização cronológica e análise das instruções normativas da Receita Federal do Brasil nos últimos 60 anos.',
    en: 'Chronological organization and analysis of Brazilian Federal Revenue regulatory instructions over the last 60 years.',
  },
  20: {
    'pt-BR':
      'Visualização interativa que ilustra a mobilidade de cargos entre os servidores públicos federais.',
    en: 'Interactive visualization illustrating job mobility among federal public servants.',
  },
  21: {
    'pt-BR':
      'Mapeamos o aumento histórico dos focos de queimada em São Paulo, em agosto de 2024.',
    en: 'We mapped the historical increase in fire outbreaks in São Paulo in August 2024.',
  },
  22: {
    'pt-BR':
      'A partir dos dados do Censo Demográfico de 2022, mostramos como o recorte racial muda o mapa do país.',
    en: 'Based on 2022 Demographic Census data, we show how the racial breakdown reshapes the country’s map.',
  },
  23: {
    'pt-BR':
      'Quais são os resultados da implementação da Política de Cotas, dez anos após sua aprovação?',
    en: 'What are the results of implementing the Quota Policy, ten years after its approval?',
  },
  24: {
    'pt-BR':
      'Por que a diagonal Bahia-Mato Grosso virou o eixo solar do continente?',
    en: 'Why did the Bahia-Mato Grosso diagonal become the continent’s solar axis?',
  },
  26: {
    'pt-BR':
      'Investigações sobre gênero, maternidade e estado civil, e seus impactos nas taxas de aprovação e de abstenção do CPNU.',
    en: 'Investigations on gender, motherhood and marital status, and their impacts on CPNU approval and abstention rates.',
  },
};

export function mapBibliotecaToStoryCard(
  story: BibliotecaResponse,
  locale: AppLocale,
): StoryCard {
  return {
    id: story.id,
    title: story.titulo,
    dek: STORY_DEK_BY_ID[story.id]?.[locale] ?? story.descricao?.trim() ?? '',
    link: story.link,
    author: story.autor,
    dateLabel: formatStoryDate(story.dataPublicacao, locale),
    views: story.visualizacoes ?? 0,
    viewsLabel: formatStoryViews(story.visualizacoes ?? 0, locale),
    publishedAt: new Date(story.dataPublicacao).getTime() || 0,
    tags: (story.tags ?? []).map((t) => t.nome).filter(Boolean),
  };
}

const REGBR_STORY_IDS = new Set([15, 16, 17, 18, 19]);

/** Ordem editorial RegBR quando publicados no mesmo dia. */
const REGBR_STORY_ORDER: Partial<Record<number, number>> = {
  15: 0, // complexidade-linguistica
  17: 1, // influencia-industria
  18: 2, // popularidade
  16: 3, // restritividade
  19: 4, // producao-normativa
};

function isRegbrStory(id: number): boolean {
  return REGBR_STORY_IDS.has(id);
}

function regbrStoryOrder(id: number): number {
  return REGBR_STORY_ORDER[id] ?? 99;
}

function compareByPublishedAt(a: StoryCard, b: StoryCard): number {
  const dateDiff = b.publishedAt - a.publishedAt;
  if (dateDiff !== 0) return dateDiff;

  const aRegbr = isRegbrStory(a.id);
  const bRegbr = isRegbrStory(b.id);
  if (aRegbr !== bRegbr) return aRegbr ? -1 : 1;

  if (aRegbr && bRegbr) {
    const orderDiff = regbrStoryOrder(a.id) - regbrStoryOrder(b.id);
    if (orderDiff !== 0) return orderDiff;
  }

  return b.views - a.views;
}

export function sortStoryCards(
  stories: StoryCard[],
  sort: DatastorySortKey,
): StoryCard[] {
  const list = [...stories];
  if (sort === 'popular') {
    return list.sort((a, b) => b.views - a.views);
  }
  if (sort === 'alpha') {
    return list.sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'));
  }
  return list.sort(compareByPublishedAt);
}

export function filterStoryCards(
  stories: StoryCard[],
  query: string,
  activeTags: string[],
): StoryCard[] {
  const q = query.trim().toLowerCase();
  let list = stories;

  if (activeTags.length > 0) {
    list = list.filter((s) => s.tags.some((tag) => activeTags.includes(tag)));
  }

  if (!q) return list;

  return list.filter((s) =>
    [s.title, s.dek, s.author, ...s.tags].join(' ').toLowerCase().includes(q),
  );
}

export function deriveStoryTags(
  stories: StoryCard[],
): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const story of stories) {
    for (const tag of story.tags) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}
